import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { TelematicsProviderSource } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { TelematicsFleet } from '~hooks/useTelematicsFleets/models';
import useTelematicsFleets from '~hooks/useTelematicsFleets/useTelematicsFleets';
import { useStores } from '~store';

// Wrapped in observer so the `currentCompanies` fallback below stays reactive:
// switching the header company selector must re-evaluate the checkbox while a
// new order's company field is still unset.
function MovingSiteSyncCheckbox() {
  const { control } = useFormContext();
  const { getTelematicsFleets } = useTelematicsFleets();
  const { userStore } = useStores();
  const watchedCompany = useWatch({ control, name: 'company' });
  const [telematicsFleets, setTelematicsFleets] = useState<TelematicsFleet[]>([]);

  useEffect(() => {
    const fetchTelematicsFleets = async () => {
      try {
        const fleets = await getTelematicsFleets();
        setTelematicsFleets(fleets ?? []);
      } catch {
        // getTelematicsFleets already surfaces the error; leave the checkbox hidden.
      }
    };

    void fetchTelematicsFleets();
  }, [getTelematicsFleets]);

  // The daily sync operates on orders belonging to the fleet owner's company
  // regardless of who dispatched them, so gate the checkbox on the order's
  // company (falling back to the selected company while a new order's company
  // field is still unset) rather than the dispatcher's login home company.
  const orderCompanyId = watchedCompany?.id ?? userStore.currentCompanies[0]?.id;

  const orderCompanyHasPaverTrackerFleet = telematicsFleets.some(
    (fleet) =>
      fleet.providerSource === TelematicsProviderSource.PAVER_TRACKER &&
      fleet.companyId === orderCompanyId,
  );

  if (!orderCompanyHasPaverTrackerFleet) {
    return null;
  }

  return (
    <Tooltip title={t('order.form.moving_site_sync_enabled_tooltip')}>
      <Box sx={{ mt: 1 }}>
        <CheckboxInput
          control={control}
          label={t('order.form.moving_site_sync_enabled')}
          name="movingSiteSyncEnabled"
          sx={{
            [`& .${formControlLabelClasses.root}`]: { mr: 0 },
            [`& .${checkboxClasses.root}`]: { pr: '2px', py: 0 },
            [`& .${typographyClasses.root}`]: {
              fontSize: '12px',
              fontWeight: 400,
            },
            width: 'fit-content',
          }}
        />
      </Box>
    </Tooltip>
  );
}

export default observer(MovingSiteSyncCheckbox);
